import { useEffect, useState } from 'react';
import { apiUrl } from '../../utils/apiBase';

/**
 * Aviso de fornecedor novo — pedido do André para os fluxos de compras.
 *
 * Três pedaços:
 *   - `AvisoFornecedorNovo`: no formulário, enquanto a pessoa digita. Avisa se
 *     o nome nunca apareceu em card nenhum e, se houver um nome quase igual já
 *     usado, oferece trocar por ele (1 em cada 4 "novos" era erro de grafia).
 *   - `ModalCienciaFornecedorNovo`: na hora de salvar — tanto ao criar o card
 *     quanto ao digitar o fornecedor num card já aberto, que é como a maioria
 *     deles entra. Pergunta se a pessoa está ciente; confirmando, o card salva e
 *     a ciência é gravada nele (quem, quando, qual fornecedor). Não impede
 *     salvar — só exige o clique, e enquanto não vier ele o card não fecha.
 *   - `SeloFornecedorNovo`: no histórico. Aparece quando este card é o primeiro
 *     a usar aquele fornecedor, e mostra quem deu ciência. Sem ciência, diz o
 *     motivo que dá para provar (card anterior ao aviso, automação) e nada além.
 *
 * "Novo" é decidido no servidor (`fornecedor_novo_routes.py`), a partir dos
 * cards — vale para card criado por automação e para os antigos. Se a
 * verificação falhar, nada aparece e nada é bloqueado.
 */

export interface CienciaFornecedorNovo {
  fornecedor: string;
  campoId: string;
  cienteEm: string;
  cientePor: string | null;
  cientePorNome: string | null;
}

interface Parecido { nome: string; cards: number; similaridade: number }

/**
 * Desde quando o aviso existe. Card nascido antes disto nunca teve como pedir
 * ciência — é a única situação em que "sem registro" não acusa ninguém. Depois
 * desta data, sem registro quer dizer que o fornecedor entrou por um caminho que
 * não perguntou, e o selo tem que dizer isso sem inventar o motivo.
 */
const INICIO_DO_AVISO = new Date('2026-09-11T00:00:00');

/** createdAt chega como Date, texto ISO ou {$date}. */
const comoData = (valor: any) => {
  if (!valor) return null;
  const d = new Date(valor?.$date || valor);
  return isNaN(d.getTime()) ? null : d;
};

const dataHora = (d: Date) =>
  d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** HTML do editor de texto -> texto simples */
export const textoLimpoFornecedor = (html: any) =>
  String(html || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

/** Para saber se o nome mudou depois da ciência: ignora caixa, acento e espaço. */
const chaveSimples = (texto: any) =>
  textoLimpoFornecedor(texto).normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ');

/**
 * A ciência já dada vale para o nome que está no campo agora?
 * Se a pessoa trocou o fornecedor depois de confirmar, precisa confirmar de novo.
 */
export const cienciaValePara = (ciencia: CienciaFornecedorNovo | null | undefined, nomeAtual: any) =>
  !!ciencia && chaveSimples(ciencia.fornecedor) === chaveSimples(nomeAtual) && chaveSimples(nomeAtual) !== '';

/** Consulta o servidor. null = não deu para saber (a tela segue sem avisar). */
export const verificarFornecedor = async (nome: string, ignorarCardId?: string, limiteMs = 4000) => {
  const limpo = textoLimpoFornecedor(nome);
  if (limpo.length < 3) return null;
  const controle = new AbortController();
  const corte = setTimeout(() => controle.abort(), limiteMs);
  try {
    const params = new URLSearchParams({ nome: limpo });
    if (ignorarCardId) params.set('ignorarCardId', ignorarCardId);
    const r = await fetch(apiUrl(`/fornecedores/verificar?${params}`), { signal: controle.signal });
    const j = await r.json();
    return j?.success && !j.vazio ? j : null;
  } catch {
    return null;
  } finally {
    clearTimeout(corte);
  }
};

const plural = (n: number) => `${n} card${n === 1 ? '' : 's'}`;

export const AvisoFornecedorNovo = ({
  nome, ignorarCardId, onUsarNome,
}: { nome: string; ignorarCardId?: string; onUsarNome?: (nome: string) => void }) => {
  const [resultado, setResultado] = useState<any>(null);
  const limpo = textoLimpoFornecedor(nome);

  useEffect(() => {
    if (limpo.length < 3) { setResultado(null); return; }
    let ativo = true;
    // espera a pessoa parar de digitar: sem isso, cada tecla vira uma consulta
    const espera = setTimeout(async () => {
      const r = await verificarFornecedor(limpo, ignorarCardId);
      if (ativo) setResultado(r);
    }, 700);
    return () => { ativo = false; clearTimeout(espera); };
  }, [limpo, ignorarCardId]);

  if (!resultado) return null;

  if (!resultado.novo) {
    return <p className="mt-1 text-[11px] text-gray-500">✓ Fornecedor já usado em {plural(resultado.cards)}</p>;
  }

  return (
    <div className="mt-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
      <p className="text-[11px] font-semibold text-amber-800">⚠️ Fornecedor novo no Escalone</p>
      <p className="text-[11px] text-amber-700">
        Nunca apareceu em nenhum card. Ao criar, vamos pedir sua ciência — ela fica registrada no card.
      </p>
      {(resultado.parecidos || []).length > 0 && (
        <div className="mt-1.5 space-y-1">
          <p className="text-[11px] text-amber-800">Não seria um destes, escrito de outro jeito?</p>
          {resultado.parecidos.map((p: Parecido) => (
            <button key={p.nome} type="button" onClick={() => onUsarNome?.(p.nome)}
              className="block w-full text-left text-[11px] px-2 py-1 rounded bg-white border border-amber-200 hover:border-amber-400 text-gray-800">
              usar <span className="font-semibold">{p.nome}</span>
              <span className="text-gray-500"> · {plural(p.cards)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ModalCienciaFornecedorNovo = ({
  fornecedor, parecidos = [], onCiente, onVoltar, onUsarNome,
}: {
  fornecedor: string;
  parecidos?: Parecido[];
  onCiente: () => void;
  onVoltar: () => void;
  onUsarNome?: (nome: string) => void;
}) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onVoltar}>
    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
      <div className="px-5 pt-5">
        <div className="flex items-center gap-2 text-amber-700">
          <span className="text-lg">⚠️</span>
          <h3 className="text-base font-bold text-gray-900">Fornecedor novo no Escalone</h3>
        </div>
        <p className="mt-2 text-sm text-gray-700">
          <span className="font-semibold">{fornecedor}</span> nunca apareceu em nenhum card.
        </p>
        <p className="mt-1 text-sm text-gray-900 font-medium">Você está ciente de que é um fornecedor novo?</p>
        <p className="mt-2 text-[11px] text-gray-500">
          Confirmando, o card é criado normalmente e a sua ciência fica registrada nele, com seu nome e o horário.
        </p>

        {parecidos.length > 0 && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5 space-y-1">
            <p className="text-[11px] text-amber-800">Antes: não seria um destes, escrito de outro jeito?</p>
            {parecidos.map((p) => (
              <button key={p.nome} type="button" onClick={() => onUsarNome?.(p.nome)}
                className="block w-full text-left text-[12px] px-2 py-1 rounded bg-white border border-amber-200 hover:border-amber-400 text-gray-800">
                usar <span className="font-semibold">{p.nome}</span>
                <span className="text-gray-500"> · {plural(p.cards)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
        <button type="button" onClick={onVoltar}
          className="px-4 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 font-medium">
          Voltar e revisar
        </button>
        <button type="button" onClick={onCiente}
          className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium">
          Estou ciente, criar card
        </button>
      </div>
    </div>
  </div>
);

export const SeloFornecedorNovo = ({
  cardId, ciencia, criadoEm, criadoPor,
}: {
  cardId?: string;
  ciencia?: CienciaFornecedorNovo | null;
  /** createdAt do card — para não chamar de "antigo" um card de hoje. */
  criadoEm?: any;
  /** 'automacao' nos cards que nascem sem formulário. */
  criadoPor?: string;
}) => {
  const [dados, setDados] = useState<any>(null);

  useEffect(() => {
    if (!cardId) return;
    let ativo = true;
    fetch(apiUrl(`/fornecedores/card/${encodeURIComponent(cardId)}`))
      .then((r) => r.json())
      .then((j) => { if (ativo) setDados(j?.success && j.aplicavel ? j : null); })
      .catch(() => undefined);
    return () => { ativo = false; };
  }, [cardId]);

  // a ciência é registro de auditoria: aparece mesmo se o cálculo mudar depois
  if (!dados?.novo && !ciencia) return null;

  const quando = comoData(ciencia?.cienteEm);
  const quandoTexto = quando ? dataHora(quando) : null;

  // Sem ciência: dizer só o que dá para saber. Antes isto afirmava "card criado
  // antes do aviso existir ou por automação" em qualquer caso — e num card de
  // hoje, criado pelo formulário, as duas metades da frase eram mentira.
  const nascimento = comoData(criadoEm);
  const motivoSemCiencia = nascimento && nascimento < INICIO_DO_AVISO
    ? `Sem registro de ciência — card criado em ${dataHora(nascimento)}, antes do aviso existir.`
    : String(criadoPor || '').toLowerCase() === 'automacao'
      ? 'Sem registro de ciência — card criado por automação, sem ninguém para perguntar.'
      : 'Sem registro de ciência — este fornecedor entrou sem ninguém confirmar.';

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-800">
        <span>⚠️</span>
        <span>Fornecedor novo no Escalone</span>
      </div>
      <div className="text-[11px] text-amber-800/90 mt-0.5">
        <span className="font-semibold">{dados?.fornecedor || ciencia?.fornecedor}</span> apareceu pela primeira vez neste card.
      </div>
      <div className="text-[10px] mt-1">
        {ciencia ? (
          <span className="text-emerald-700">
            ✓ Ciência dada por <span className="font-semibold">{ciencia.cientePorNome || 'usuário não identificado'}</span>
            {quandoTexto ? ` em ${quandoTexto}` : ''}
          </span>
        ) : (
          <span className="text-amber-700/80">{motivoSemCiencia}</span>
        )}
      </div>
      {(dados?.parecidos || []).length > 0 && (
        <div className="text-[10px] text-amber-700/80 mt-1">
          Parecido com {dados.parecidos.map((p: Parecido) => `${p.nome} (${plural(p.cards)})`).join(', ')}
          {' '}— pode ser o mesmo fornecedor escrito diferente.
        </div>
      )}
    </div>
  );
};
